/* eslint-disable consistent-return */

import { ENV, ENV_ENUM } from '@/shared/config';
import {
  zeroPad,
  formatPhoneNumberWithCode,
  dataToStringForNameReport,
  gettextReportType,
  auth,
} from '@/shared/lib';
import { truncateTextAndRowCount } from '@/shared/lib/reportDraw';
import { t } from 'ttag';
import _ from 'lodash';
import { degrees, rgb } from 'pdf-lib';
import {
  AI_REPORT_LAYOUT,
  AI_REPORT_LAYOUT_VER100,
  EOU_REPORT_LAYOUT,
  EOU_REPORT_LAYOUT_VER200,
} from '../config/inboxDetails';

/**
 * Builds the download file name of an inbox report PDF. Event reports are named Event plus facility, patient, zero-padded study and event ids and the date from dataToStringForNameReport, while other reports use their type (Instant for UNREVIEWED, Summary without a reportId) with facility, patient, study id, optional report id and the study start date.
 *
 * @param data - Report record with its study, facility and patient.
 * @returns File name ending in .pdf for the report.
 */
const getReportFileName = (data: any) => {
  const { study } = data;
  const { facility } = study;
  const { patient } = study?.info || {};

  if (data?.eventFid) {
    return `Event-${facility?.name}-${patient?.patientId}-${zeroPad(study?.friendlyId)}-${zeroPad(data?.eventFid)}-${dataToStringForNameReport(
      {
        startStudyDate: study?.start,
        isoDate: data?.start,
        timezone: study?.timezone,
        facilityCountry: facility?.contact?.country,
        facilityTimezone: facility?.timezone,
      },
    )}.pdf`;
  }

  const reportType = data?.reportId
    ? data?.type === 'UNREVIEWED'
      ? 'Instant'
      : data?.type
    : 'Summary';
  return `${reportType}-${facility?.name}-${patient?.patientId}-${zeroPad(study?.friendlyId)}${data?.reportId ? `-${zeroPad(data?.reportId)}` : ''}-${dataToStringForNameReport(
    {
      startStudyDate: study?.start,
      isoDate: study?.start,
      timezone: study?.timezone,
      facilityCountry: facility?.contact?.country,
      facilityTimezone: facility?.timezone,
    },
  )}.pdf`;
};

/**
 * Works out where and how to overwrite the interpretation, signature and date areas of a report
 * PDF for its report kind and format version. Picks black-and-white test colors or the format's
 * text colors, then returns the rectangle, text, title, name, date and image draw options for
 * notification-event, AI or instant, ARCS, notification, event, interim, EOU or default layouts.
 *
 * @param props - Font, test flag, format versions, report kind flags and profile that select the layout.
 * @returns The pdf-lib draw options for each overwritten area of the report.
 */
const getModifyPdfReportOptions = ({
  customFont,
  isTest,
  formatVersion,
  aiFormatVersion,
  isNewFormat,
  isUploadedAiReport,
  isUploadedArcsReport,
  isInstantReport,
  isNotificationReport,
  isEventReport,
  isInterimReport,
  isEouReport,
  isMctReport,
  isMctOrMctPeek,
  isMctOnly,
  profile,
  isNewNotiEventReport,
}: {
  customFont: any;
  isTest: boolean;
  formatVersion: string;
  aiFormatVersion: string;
  isNewFormat: boolean;
  isUploadedAiReport: boolean;
  isUploadedArcsReport: boolean;
  isInstantReport: boolean;
  isNotificationReport: boolean;
  isEventReport: boolean;
  isInterimReport: boolean;
  isEouReport: boolean;
  isMctReport: boolean;
  isMctOrMctPeek: boolean;
  isMctOnly: boolean;
  profile: string;
  isNewNotiEventReport: boolean;
}) => {
  // *: Padding are 5 (left) and 5 (top)
  const drawRectangleEventY = 42;
  const drawTextEventY = 157;
  const drawImageEventY = 60;

  const drawRectangleMCTReportY = 77;
  const drawTextMCTReportY = 191;
  const drawImageMCTReportY = 95;

  const drawRectangleReportY = 98;
  const drawTextReportY = 212;
  const drawImageReportY = 116;

  const textRectangleOptions = isTest
    ? {
        // *: Draw black rectangle for test purpose
        color: rgb(0, 0, 0),
        borderColor: rgb(0, 0, 0),
      }
    : {
        color: rgb(1, 1, 1),
        borderColor: rgb(1, 1, 1),
      };

  const textOptions = isTest
    ? {
        // *: Draw white text for test purpose
        color: rgb(1, 1, 1),
      }
    : formatVersion >= '2.0.0'
      ? {
          color: rgb(0.13725490196078433, 0.12156862745098039, 0.12549019607843137),
        }
      : {
          color: rgb(0.11372549019607843, 0.1843137254901961, 0.2784313725490196),
        };
  const dateRectangleOptions = isTest
    ? {
        // *: Draw black rectangle for test purpose
        color: rgb(0, 0, 0),
        borderColor: rgb(0, 0, 0),
      }
    : {
        color: rgb(1, 1, 1),
        borderColor: rgb(1, 1, 1),
      };
  const dateTextOptions = isTest
    ? {
        // *: Draw white text for test purpose
        color: rgb(1, 1, 1),
      }
    : formatVersion >= '2.0.0'
      ? {
          color: rgb(0.13725490196078433, 0.12156862745098039, 0.12549019607843137),
        }
      : {
          color: rgb(0.11372549019607843, 0.1843137254901961, 0.2784313725490196),
        };
  if (isNewNotiEventReport) {
    return {
      drawRectangleOptions: {
        x: 17,
        y: 340 - (isNotificationReport ? 221.2 : 0),
        ...textRectangleOptions,
        borderWidth: 1,
        width: 578,
        height: 125,
      },
      drawTextOptions: {
        x: 22,
        y: 453 - (isNotificationReport ? 221.2 : 0),
        size: 9,
        font: customFont,
        ...textOptions,
        lineHeight: 11.25,
        maxWidth: 564,
      },
      drawDateTextOptions: {
        x: 22,
        y: 328 - (isNotificationReport ? 221.2 : 0),
        size: 9,
        font: customFont,
        ...dateTextOptions,
        lineHeight: 15.12,
      },
      drawTitleTextOptions: {
        x: 22,
        y: 317 - (isNotificationReport ? 221.2 : 0),
        size: 9,
        font: customFont,
        ...textOptions,
        lineHeight: 15.12,
      },
      drawImageOptions: {
        x: 22,
        y: 283 - (isNotificationReport ? 221.2 : 0),
        width: 54,
        height: 28,
      },
      drawNameTextOptions: {
        x: 22,
        y: 271 - (isNotificationReport ? 221.2 : 0),
        size: 9,
        font: customFont,
        ...textOptions,
        lineHeight: 15.12,
      },
      drawDateRectangleOptions: {
        x: 17,
        y: 266 - (isNotificationReport ? 221.2 : 0),
        ...dateRectangleOptions,
        borderWidth: 1,
        width: 270,
        height: 75,
      },
    };
  }

  // *: Copied/Uploaded AI report & Instant report
  if (isNewFormat || isUploadedAiReport || isInstantReport || (isInterimReport && !!profile)) {
    if (aiFormatVersion >= '1.0.0') {
      return {
        drawRectangleOptions: {
          x: 15,
          y: 163,
          ...textRectangleOptions,
          borderWidth: 1,
          width: 383,
          height: 245,
        },
        drawTextOptions: {
          x: 17,
          y: 396,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 13.58,
          maxWidth: 385,
        },
        drawImageOptions: {
          x: 16,
          y: 84,
          width: 80,
          height: 33,
        },
        drawDateRectangleOptions: {
          x: 15,
          y: 68,
          ...dateRectangleOptions,
          borderWidth: 1,
          width: 200,
          height: 78,
        },
        drawDateTextOptions: {
          x: 16,
          y: 136,
          size: 9,
          font: customFont,
          ...dateTextOptions,
          lineHeight: 12,
        },
        drawTitleTextOptions: {
          x: 16,
          y: 124,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 12,
        },
        drawNameTextOptions: {
          x: 16,
          y: 71,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 12,
        },
      };
    }

    return {
      drawRectangleOptions: {
        x: 20,
        y: 135,
        ...textRectangleOptions,
        borderWidth: 1,
        width: 363,
        height: 245,
      },
      drawTextOptions: {
        x: 22,
        y: 368,
        size: 9,
        font: customFont,
        ...textOptions,
        lineHeight: 13.58,
        maxWidth: 365,
      },
      drawImageOptions: {
        x: 21,
        y: 72,
        width: 80,
        height: 33,
      },
      drawDateRectangleOptions: {
        x: 20,
        y: 56,
        ...dateRectangleOptions,
        borderWidth: 1,
        width: 200,
        height: 78,
      },
      drawDateTextOptions: {
        x: 22,
        y: 124,
        size: 9,
        font: customFont,
        ...dateTextOptions,
        lineHeight: 12,
      },
      drawTitleTextOptions: {
        x: 22,
        y: 112,
        size: 9,
        font: customFont,
        ...textOptions,
        lineHeight: 12,
      },
      drawNameTextOptions: {
        x: 22,
        y: 59,
        size: 9,
        font: customFont,
        ...textOptions,
        lineHeight: 12,
      },
    };
  }

  // *: Uploaded ARCS report
  if (isUploadedArcsReport) {
    return {
      drawRectangleOptions: {
        x: 548,
        y: 326,
        ...textRectangleOptions,
        borderWidth: 1,
        width: 315,
        height: 157,
        rotate: degrees(90),
      },
      drawTextOptions: {
        x: 403,
        y: 330,
        size: 8,
        font: customFont,
        ...textOptions,
        lineHeight: 12,
        maxWidth: 309,
        rotate: degrees(90),
      },
      drawImageOptions: {
        x: 530,
        y: 645,
        width: 132.5,
        height: 55,
        rotate: degrees(90),
      },
      drawDateRectangleOptions: {
        x: 473,
        y: 645,
        ...dateRectangleOptions,
        borderWidth: 1,
        width: 135,
        height: 10,
        rotate: degrees(90),
      },
      drawDateTextOptions: {
        x: 470,
        y: 645,
        size: 7.5,
        font: customFont,
        ...dateTextOptions,
        lineHeight: 12,
        rotate: degrees(90),
      },
    };
  }

  // *: Notification report
  if (isNotificationReport) {
    if (formatVersion >= '2.0.1') {
      return {
        drawRectangleOptions: {
          x: 311,
          y: 172,
          ...textRectangleOptions,
          borderWidth: 1,
          width: 281.5,
          height: 67.86,
        },
        drawTextOptions: {
          x: 313,
          y: 232,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 11.25,
          maxWidth: 277.5,
        },
        drawImageOptions: {
          x: 313,
          y: 111,
          width: 68.8,
          height: 28,
        },
        drawDateRectangleOptions: {
          x: 311,
          y: 94,
          ...dateRectangleOptions,
          borderWidth: 1,
          width: 270,
          height: 75,
        },
        drawDateTextOptions: {
          x: 313,
          y: 160,
          size: 9,
          font: customFont,
          ...dateTextOptions,
          lineHeight: 15.12,
        },
        drawTitleTextOptions: {
          x: 313,
          y: 147,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 15.12,
        },
        drawNameTextOptions: {
          x: 313,
          y: 102,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 15.12,
        },
      };
    }

    const dateRectangleReportY = 176;
    return {
      drawRectangleOptions: {
        x: 352,
        y: drawRectangleReportY,
        ...textRectangleOptions,
        borderWidth: 1,
        width: 290,
        height: 126,
      },
      drawTextOptions: {
        x: 355,
        y: drawTextReportY,
        size: 10,
        font: customFont,
        ...textOptions,
        lineHeight: 12,
        maxWidth: 284,
      },
      drawImageOptions: {
        x: 643,
        y: drawImageReportY,
        width: 132.5,
        height: 55,
      },
      drawDateRectangleOptions: {
        x: 643,
        y: dateRectangleReportY,
        color: rgb(1, 1, 1),
        borderColor: rgb(1, 1, 1),
        // *: Draw black rectangle for test purpose
        // color: rgb(0, 0, 0),
        // borderColor: rgb(0, 0, 0),
        borderWidth: 1,
        width: 140,
        height: 12,
      },
      drawDateTextOptions: {
        x: 643,
        y: dateRectangleReportY + 2,
        size: 7.5,
        font: customFont,
        color: rgb(0, 0, 0),
        // *: Draw white text for test purpose
        // color: rgb(1, 1, 1),
        lineHeight: 12,
      },
    };
  }

  // *: Event report
  if (isEventReport) {
    if (formatVersion >= '2.0.1') {
      return {
        drawRectangleOptions: {
          x: 311,
          y: 393,
          ...textRectangleOptions,
          borderWidth: 1,
          width: 281.5,
          height: 67.86,
        },
        drawTextOptions: {
          x: 313,
          y: 453,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 11.25,
          maxWidth: 277.5,
        },
        drawImageOptions: {
          x: 313,
          y: 333,
          width: 68.8,
          height: 28,
        },
        drawDateRectangleOptions: {
          x: 311,
          y: 315,
          ...dateRectangleOptions,
          borderWidth: 1,
          width: 270,
          height: 75,
        },
        drawDateTextOptions: {
          x: 313,
          y: 381,
          size: 9,
          font: customFont,
          ...dateTextOptions,
          lineHeight: 15.12,
        },
        drawTitleTextOptions: {
          x: 313,
          y: 367,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 15.12,
        },
        drawNameTextOptions: {
          x: 313,
          y: 322,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 15.12,
        },
      };
    }

    return {
      drawRectangleOptions: {
        x: 352,
        y: drawRectangleEventY,
        ...textRectangleOptions,
        borderWidth: 1,
        width: 290,
        height: 126,
      },
      drawTextOptions: {
        x: 355,
        y: drawTextEventY,
        size: 10,
        font: customFont,
        ...textOptions,
        lineHeight: 12,
        maxWidth: 284,
      },
      drawImageOptions: {
        x: 643,
        y: drawImageEventY,
        width: 132.5,
        height: 55,
      },
      drawDateRectangleOptions: {
        x: 643,
        y: 120,
        color: rgb(1, 1, 1),
        borderColor: rgb(1, 1, 1),
        // *: Draw black rectangle for test purpose
        // color: rgb(0, 0, 0),
        // borderColor: rgb(0, 0, 0),
        borderWidth: 1,
        width: 140,
        height: 12,
      },
      drawDateTextOptions: {
        x: 643,
        y: 122,
        size: 7.5,
        font: customFont,
        color: rgb(0, 0, 0),
        // *: Draw white text for test purpose
        // color: rgb(1, 1, 1),
        lineHeight: 12,
      },
    };
  }

  // *: Interim report
  if (isInterimReport) {
    if (formatVersion >= '2.0.0') {
      if (isMctOrMctPeek) {
        return {
          drawRectangleOptions: {
            x: 311,
            y: 129,
            ...textRectangleOptions,
            borderWidth: 1,
            width: 281.5,
            height: 67.86,
          },
          drawTextOptions: {
            x: 313,
            y: 189,
            size: 9,
            font: customFont,
            ...textOptions,
            lineHeight: 10.75,
            maxWidth: 277.5,
          },
          drawImageOptions: {
            x: 313,
            y: 70,
            width: 68.8,
            height: 28,
          },
          drawDateRectangleOptions: {
            x: 311,
            y: 51,
            ...dateRectangleOptions,
            borderWidth: 1,
            width: 270,
            height: 75,
          },
          drawDateTextOptions: {
            x: 313,
            y: 117,
            size: 9,
            font: customFont,
            ...dateTextOptions,
            lineHeight: 15.12,
          },
          drawTitleTextOptions: {
            x: 313,
            y: 104,
            size: 9,
            font: customFont,
            ...textOptions,
            lineHeight: 15.12,
          },
          drawNameTextOptions: {
            x: 313,
            y: 59,
            size: 9,
            font: customFont,
            ...textOptions,
            lineHeight: 15.12,
          },
        };
      }

      return {
        drawRectangleOptions: {
          x: 311,
          y: 174,
          ...textRectangleOptions,
          borderWidth: 1,
          width: 281.5,
          height: 67.86,
        },
        drawTextOptions: {
          x: 313,
          y: 234,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 10.75,
          maxWidth: 277.5,
        },
        drawImageOptions: {
          x: 313,
          y: 115,
          width: 68.8,
          height: 28,
        },
        drawDateRectangleOptions: {
          x: 311,
          y: 96,
          ...dateRectangleOptions,
          borderWidth: 1,
          width: 270,
          height: 75,
        },
        drawDateTextOptions: {
          x: 313,
          y: 162,
          size: 9,
          font: customFont,
          ...dateTextOptions,
          lineHeight: 15.12,
        },
        drawTitleTextOptions: {
          x: 313,
          y: 149,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 15.12,
        },
        drawNameTextOptions: {
          x: 313,
          y: 104,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 15.12,
        },
      };
    }

    const dateRectangleMCTReportY = 155;
    const dateRectangleReportY = 176;
    if (isMctOrMctPeek) {
      return {
        drawRectangleOptions: {
          x: 352,
          y: drawRectangleMCTReportY,
          ...textRectangleOptions,
          borderWidth: 1,
          width: 290,
          height: 126,
        },
        drawTextOptions: {
          x: 355,
          y: drawTextMCTReportY,
          size: 10,
          font: customFont,
          ...textOptions,
          lineHeight: 12,
          maxWidth: 284,
        },
        drawImageOptions: {
          x: 643,
          y: drawImageMCTReportY,
          width: 132.5,
          height: 55,
        },
        drawDateRectangleOptions: {
          x: 643,
          y: dateRectangleMCTReportY,
          color: rgb(1, 1, 1),
          borderColor: rgb(1, 1, 1),
          // *: Draw black rectangle for test purpose
          // color: rgb(0, 0, 0),
          // borderColor: rgb(0, 0, 0),
          borderWidth: 1,
          width: 140,
          height: 12,
        },
        drawDateTextOptions: {
          x: 643,
          y: dateRectangleMCTReportY + 2,
          size: 7.5,
          font: customFont,
          color: rgb(0, 0, 0),
          // *: Draw white text for test purpose
          // color: rgb(1, 1, 1),
          lineHeight: 12,
        },
      };
    }

    return {
      drawRectangleOptions: {
        x: 352,
        y: drawRectangleReportY,
        ...textRectangleOptions,
        borderWidth: 1,
        width: 290,
        height: 126,
      },
      drawTextOptions: {
        x: 355,
        y: drawTextReportY,
        size: 10,
        font: customFont,
        ...textOptions,
        lineHeight: 12,
        maxWidth: 284,
      },
      drawImageOptions: {
        x: 643,
        y: drawImageReportY,
        width: 132.5,
        height: 55,
      },
      drawDateRectangleOptions: {
        x: 643,
        y: dateRectangleReportY,
        color: rgb(1, 1, 1),
        borderColor: rgb(1, 1, 1),
        // *: Draw black rectangle for test purpose
        // color: rgb(0, 0, 0),
        // borderColor: rgb(0, 0, 0),
        borderWidth: 1,
        width: 140,
        height: 12,
      },
      drawDateTextOptions: {
        x: 643,
        y: dateRectangleReportY + 2,
        size: 7.5,
        font: customFont,
        color: rgb(0, 0, 0),
        // *: Draw white text for test purpose
        // color: rgb(1, 1, 1),
        lineHeight: 12,
      },
    };
  }

  // *: EoU report
  if (isEouReport) {
    if (formatVersion >= '2.0.1') {
      if (isMctOnly) {
        return {
          drawRectangleOptions: {
            x: 311,
            y: 129,
            ...textRectangleOptions,
            borderWidth: 1,
            width: 281.5,
            height: 67.86,
          },
          drawTextOptions: {
            x: 313,
            y: 189,
            size: 9,
            font: customFont,
            ...textOptions,
            lineHeight: 10.75,
            maxWidth: 277.5,
          },
          drawImageOptions: {
            x: 313,
            y: 69,
            width: 68.8,
            height: 28,
          },
          drawDateRectangleOptions: {
            x: 311,
            y: 51,
            ...dateRectangleOptions,
            borderWidth: 1,
            width: 270,
            height: 75,
          },
          drawDateTextOptions: {
            x: 313,
            y: 117,
            size: 9,
            font: customFont,
            ...dateTextOptions,
            lineHeight: 15.12,
          },
          drawTitleTextOptions: {
            x: 313,
            y: 104,
            size: 9,
            font: customFont,
            ...textOptions,
            lineHeight: 15.12,
          },
          drawNameTextOptions: {
            x: 313,
            y: 59,
            size: 9,
            font: customFont,
            ...textOptions,
            lineHeight: 15.12,
          },
        };
      }

      return {
        drawRectangleOptions: {
          x: 311,
          y: 172,
          ...textRectangleOptions,
          borderWidth: 1,
          width: 281.5,
          height: 67.86,
        },
        drawTextOptions: {
          x: 313,
          y: 232,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 10.75,
          maxWidth: 277.5,
        },
        drawImageOptions: {
          x: 313,
          y: 111,
          width: 68.8,
          height: 28,
        },
        drawDateRectangleOptions: {
          x: 311,
          y: 94,
          ...dateRectangleOptions,
          borderWidth: 1,
          width: 270,
          height: 75,
        },
        drawDateTextOptions: {
          x: 313,
          y: 160,
          size: 9,
          font: customFont,
          ...dateTextOptions,
          lineHeight: 15.12,
        },
        drawTitleTextOptions: {
          x: 313,
          y: 147,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 15.12,
        },
        drawNameTextOptions: {
          x: 313,
          y: 102,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 15.12,
        },
      };
    }

    if (formatVersion >= '2.0.0') {
      if (isMctOnly) {
        return {
          drawRectangleOptions: {
            x: 311,
            y: 145,
            ...textRectangleOptions,
            borderWidth: 1,
            width: 281.5,
            height: 77.86,
          },
          drawTextOptions: {
            x: 313,
            y: 215,
            size: 9,
            font: customFont,
            ...textOptions,
            lineHeight: 10.75,
            maxWidth: 277.5,
          },
          drawImageOptions: {
            x: 313,
            y: 77,
            width: 66.1,
            height: 27,
          },
          drawDateRectangleOptions: {
            x: 311,
            y: 121,
            ...dateRectangleOptions,
            borderWidth: 1,
            width: 270,
            height: 10,
          },
          drawDateTextOptions: {
            x: 313,
            y: 123,
            size: 8.6,
            font: customFont,
            ...dateTextOptions,
            lineHeight: 15.12,
          },
        };
      }

      return {
        drawRectangleOptions: {
          x: 311,
          y: 186,
          ...textRectangleOptions,
          borderWidth: 1,
          width: 281.5,
          height: 77.86,
        },
        drawTextOptions: {
          x: 313,
          y: 256,
          size: 9,
          font: customFont,
          ...textOptions,
          lineHeight: 10.75,
          maxWidth: 277.5,
        },
        drawImageOptions: {
          x: 313,
          y: 118,
          width: 66.1,
          height: 27,
        },
        drawDateRectangleOptions: {
          x: 311,
          y: 162,
          ...dateRectangleOptions,
          borderWidth: 1,
          width: 270,
          height: 10,
        },
        drawDateTextOptions: {
          x: 313,
          y: 164,
          size: 8.6,
          font: customFont,
          ...dateTextOptions,
          lineHeight: 15.12,
        },
      };
    }

    const dateRectangleMCTReportY = 155;
    const dateRectangleReportY = 176;
    if (isMctOnly) {
      return {
        drawRectangleOptions: {
          x: 352,
          y: drawRectangleMCTReportY,
          ...textRectangleOptions,
          borderWidth: 1,
          width: 290,
          height: 126,
        },
        drawTextOptions: {
          x: 355,
          y: drawTextMCTReportY,
          size: 10,
          font: customFont,
          ...textOptions,
          lineHeight: 12,
          maxWidth: 284,
        },
        drawImageOptions: {
          x: 643,
          y: drawImageMCTReportY,
          width: 132.5,
          height: 55,
        },
        drawDateRectangleOptions: {
          x: 643,
          y: dateRectangleMCTReportY,
          color: rgb(1, 1, 1),
          borderColor: rgb(1, 1, 1),
          // *: Draw black rectangle for test purpose
          // color: rgb(0, 0, 0),
          // borderColor: rgb(0, 0, 0),
          borderWidth: 1,
          width: 140,
          height: 12,
        },
        drawDateTextOptions: {
          x: 643,
          y: dateRectangleMCTReportY + 2,
          size: 7.5,
          font: customFont,
          color: rgb(0, 0, 0),
          // *: Draw white text for test purpose
          // color: rgb(1, 1, 1),
          lineHeight: 12,
        },
      };
    }

    return {
      drawRectangleOptions: {
        x: 352,
        y: drawRectangleReportY,
        ...textRectangleOptions,
        borderWidth: 1,
        width: 290,
        height: 126,
      },
      drawTextOptions: {
        x: 355,
        y: drawTextReportY,
        size: 10,
        font: customFont,
        ...textOptions,
        lineHeight: 12,
        maxWidth: 284,
      },
      drawImageOptions: {
        x: 643,
        y: drawImageReportY,
        width: 132.5,
        height: 55,
      },
      drawDateRectangleOptions: {
        x: 643,
        y: dateRectangleReportY,
        color: rgb(1, 1, 1),
        borderColor: rgb(1, 1, 1),
        // *: Draw black rectangle for test purpose
        // color: rgb(0, 0, 0),
        // borderColor: rgb(0, 0, 0),
        borderWidth: 1,
        width: 140,
        height: 12,
      },
      drawDateTextOptions: {
        x: 643,
        y: dateRectangleReportY + 2,
        size: 7.5,
        font: customFont,
        color: rgb(0, 0, 0),
        // *: Draw white text for test purpose
        // color: rgb(1, 1, 1),
        lineHeight: 12,
      },
    };
  }

  // TODO: Check unused below code
  if (formatVersion === '2.0.1') {
    return {
      drawRectangleOptions: {
        x: 311,
        y: isMctOrMctPeek ? 119 : 162,
        ...textRectangleOptions,
        borderWidth: 1,
        width: 281.5,
        height: 77.86,
      },
      drawTextOptions: {
        x: 313,
        y: isMctOrMctPeek ? 189 : 232,
        size: 9,
        font: customFont,
        ...textOptions,
        lineHeight: 11.25,
        maxWidth: 277.5,
      },
      drawImageOptions: {
        x: 313,
        y: isNotificationReport ? 98 : isMctOrMctPeek ? 56 : 98,
        width: 68.8,
        height: 28,
      },
      drawDateRectangleOptions: {
        x: 311,
        y: isNotificationReport ? 144 : isMctOrMctPeek ? 101 : 144,
        ...dateRectangleOptions,
        borderWidth: 1,
        width: 270,
        height: 10,
      },
      drawDateTextOptions: {
        x: 313,
        y: isNotificationReport ? 146 : isMctOrMctPeek ? 103 : 146,
        size: 9,
        font: customFont,
        ...dateTextOptions,
        lineHeight: 15.12,
      },
    };
  }

  if (formatVersion === '2.0.0') {
    return {
      drawRectangleOptions: {
        x: 311,
        y: isMctReport ? 145 : 186,
        ...textRectangleOptions,
        borderWidth: 1,
        width: 281.5,
        height: 77.86,
      },
      drawTextOptions: {
        x: 313,
        y: isMctReport ? 215 : 256,
        size: 9,
        font: customFont,
        ...textOptions,
        lineHeight: 10.75,
        maxWidth: 277.5,
      },
      drawImageOptions: {
        x: 313,
        y: isMctReport ? 77 : 118,
        width: 66.1,
        height: 27,
      },
      drawDateRectangleOptions: {
        x: 311,
        y: isMctReport ? 121 : 162,
        ...dateRectangleOptions,
        borderWidth: 1,
        width: 270,
        height: 10,
      },
      drawDateTextOptions: {
        x: 313,
        y: isMctReport ? 123 : 164,
        size: 8.6,
        font: customFont,
        ...dateTextOptions,
        lineHeight: 15.12,
      },
    };
  }

  const dateRectangleMCTReportY = 155;
  const dateRectangleReportY = 176;
  return {
    drawRectangleOptions: {
      x: 352,
      y: isMctReport ? drawRectangleMCTReportY : drawRectangleReportY,
      ...textRectangleOptions,
      borderWidth: 1,
      width: 290,
      height: 126,
    },
    drawTextOptions: {
      x: 355,
      y: isMctReport ? drawTextMCTReportY : drawTextReportY,
      size: 10,
      font: customFont,
      ...textOptions,
      lineHeight: 12,
      maxWidth: 284,
    },
    drawImageOptions: {
      x: 643,
      y: isMctReport ? drawImageMCTReportY : drawImageReportY,
      width: 132.5,
      height: 55,
    },
    drawDateRectangleOptions: {
      x: 643,
      y: isMctReport ? dateRectangleMCTReportY : dateRectangleReportY,
      color: rgb(1, 1, 1),
      borderColor: rgb(1, 1, 1),
      // *: Draw black rectangle for test purpose
      // color: rgb(0, 0, 0),
      // borderColor: rgb(0, 0, 0),
      borderWidth: 1,
      width: 140,
      height: 12,
    },
    drawDateTextOptions: {
      x: 643,
      y: isMctReport ? dateRectangleMCTReportY + 2 : dateRectangleReportY + 2,
      size: 7.5,
      font: customFont,
      color: rgb(0, 0, 0),
      // *: Draw white text for test purpose
      // color: rgb(1, 1, 1),
      lineHeight: 12,
    },
  };
};

// const checkShouldTrackAddSignatureTime = (userId: string) => {
//   const shouldTrackAddSignatureTimeUserIds =
//     ENV === ENV_ENUM.customer
//       ? [
//           '5b6aab8e830f785ae31b6946', // *: QC test user
//           '667d66cc529b9608ca4b2505',
//         ]
//       : ENV === ENV_ENUM.staging
//         ? [
//             '675fe56e9b17295304bb39e0', // *: Test user
//           ]
//         : [];
// return shouldTrackAddSignatureTimeUserIds.includes(userId);
// };
/**
 * Reports whether add-signature timing should be tracked in the running deployment. Compares the build ENV against ENV_ENUM.customer.
 *
 * @returns True only on the customer environment.
 */
const checkShouldTrackAddSignatureTime = () => ENV === ENV_ENUM.customer;

/**
 * Groups report page numbers into rows of three for the page navigation. Walks 1..n in steps of three, collecting up to three consecutive numbers per sub-array.
 *
 * @param n - Total number of pages.
 * @returns Array of sub-arrays holding at most three page numbers each.
 */
const splitPageNumber = (n: number) => {
  const result = [];
  for (let i = 1; i <= n; i += 3) {
    const sub = [];
    for (let j = i; j < i + 3 && j <= n; j += 1) {
      sub.push(j);
    }
    result.push(sub);
  }
  return result;
};

/**
 * Prints the first line of the report findings over the findings slot of every event strip in a
 * report PDF. Does nothing without strips, otherwise truncates the findings to one line, blanks
 * and redraws the slot on the cover page, and on later pages redraws it for each of up to three
 * strips per page.
 *
 * @param info - Strip count, PDF document, findings text, font and cover page to draw on.
 * @returns Nothing.
 */
const drawFindingOnEventStrip = (info: {
  totalStrips?: number;
  pdfDoc: any;
  findings?: string;
  customFont?: any;
  coverPage?: any;
}) => {
  const { totalStrips, pdfDoc, findings, customFont, coverPage } = info || {};
  if (!totalStrips) return;
  const { lines } = truncateTextAndRowCount({
    text: findings,
    font: customFont,
    fontSize: 9,
    maxWidth: 470,
    maxLines: 1,
  });

  const text = lines?.[0] || '';
  const rectangleOptions = {
    color: rgb(1, 1, 1),
    x: 116,
    width: 475,
    height: 16,
  };
  const textOptions = {
    x: 118.5,
    width: 578,
    size: 9,
    font: customFont,
    color: rgb(0, 0, 0),
  };

  coverPage.drawRectangle({
    ...rectangleOptions,
    y: 212,
  });
  coverPage.drawText(text, {
    ...textOptions,
    y: 217,
  });
  if (totalStrips > 1) {
    const pages = pdfDoc.getPages();
    const arrayNumber = splitPageNumber(totalStrips - 1);
    _.forEach(pages, (page, idx: number) => {
      if (idx > 0) {
        const currentPage = arrayNumber[idx - 1];
        if (!_.isEmpty(currentPage)) {
          _.forEach(currentPage, (x, i) => {
            const buffer = (i as any) * 217.5;
            page.drawRectangle({
              ...rectangleOptions,
              y: 685.5 - buffer,
            });
            page.drawText(text, {
              ...textOptions,
              y: 690.5 - buffer,
            });
          });
        }
      }
    });
  }
};

/**
 * Trims report findings text so it fits the ten lines available on the PDF. Wraps each line-break segment through truncateTextAndRowCount with the given font, size and maxWidth until the line budget is spent, rejoins the kept lines and replaces the last three characters with an ellipsis when content was dropped.
 *
 * @param findingsText - Raw findings text to fit.
 * @param drawTextOptions - Draw options supplying size and maxWidth.
 * @param customFont - Embedded font used to measure the text.
 * @returns Findings text limited to ten rendered lines, ellipsized when truncated.
 */
const truncateFindingsText = (findingsText: string, drawTextOptions: any, customFont: any) => {
  let formatText = findingsText;
  const breakLines = findingsText.split('\n');
  const maxLines = 10;
  let countLine = 0;
  const textArr: string[] = [];
  let isBreak = false;
  _.forEach(breakLines, (line) => {
    if (countLine < maxLines) {
      const { lines } = truncateTextAndRowCount({
        text: line,
        font: customFont,
        fontSize: drawTextOptions.size,
        maxWidth: drawTextOptions.maxWidth,
        maxLines: maxLines - countLine,
      });
      countLine += lines.length;
      textArr.push(...lines);
    } else {
      // break loop
      isBreak = true;
      return false;
    }
  });
  formatText = textArr.join('\n');
  if (formatText.length < findingsText.length && isBreak) {
    formatText = `${formatText.slice(0, -3)}...`;
  }
  return formatText;
};

/**
 * Builds the display name of a physician for report and list output. Joins firstName and lastName, returns '--' when neither is present, and appends the title after a comma unless the title is missing or isSkippTitle is set.
 *
 * @param physician - Physician holding firstName, lastName and title.
 * @param isSkippTitle - Whether the title is left off the name.
 * @returns Physician name with optional title, or '--'.
 */
const getPhysicianTitle = (
  physician: { firstName?: string; lastName?: string; title?: string },
  isSkippTitle?: boolean,
) => {
  const { firstName, lastName, title } = physician || {};
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (!name) {
    return '--';
  }
  if (!title || isSkippTitle) return `${name}` || '--';
  return `${name}, ${title}`.trim() || '--';
};

/**
 * Writes a block of text lines onto a PDF page, one below the other. Draws each line at the given
 * x with the shared text options, stepping y down by lineHeight per line.
 *
 * @param page - PDF page to draw on.
 * @param lines - Text lines to draw in order.
 * @param x - Horizontal position of every line.
 * @param y - Vertical position of the first line.
 * @param lineHeight - Distance between consecutive lines.
 * @param options - Font, size and color options applied to each line.
 * @returns Nothing.
 */
const drawLines = ({
  page,
  lines,
  x,
  y,
  lineHeight,
  options,
}: {
  page: any;
  lines: string[];
  x: number;
  y: number;
  lineHeight: number;
  options: any;
}) => {
  lines.forEach((line, idx) => {
    page.drawText(line, {
      ...options,
      x,
      y: y - idx * lineHeight,
    });
  });
};

/**
 * Resolves the phone number printed for the interpreting physician on a report. Formats the facility contact phone1 with its country when includeContactToReport is unset and the physician's own contact otherwise, falling back to '--'.
 *
 * @param physician - Interpreting physician with contact and includeContactToReport.
 * @param facilityInfo - Facility whose contact is the fallback.
 * @returns Formatted phone number, or '--'.
 */
const getInterrettingPhysicianPhone = (physician: any, facilityInfo: any) => {
  const { contact, includeContactToReport } = physician || {};
  if (!includeContactToReport) {
    return (
      formatPhoneNumberWithCode(facilityInfo?.contact?.phone1, facilityInfo?.contact?.country) ||
      '--'
    );
  }
  return formatPhoneNumberWithCode(contact?.phone1, contact?.country) || '--';
};

/**
 * Formats a contact record into the single-line address printed on reports. Returns '--' when data is missing or every part is empty, otherwise joins the street address (only while hasAddress), city, state and zip with commas.
 *
 * @param data - Contact holding address, city, state and zip.
 * @param hasAddress - Whether the street address line is included.
 * @returns Comma-joined address text, or '--'.
 */
export const generateTextAddress = (data: any, hasAddress: boolean) => {
  if (!data) {
    return '--';
  }
  const { address, city, state, zip } = data || {};
  const result = [];
  if (address && hasAddress) {
    result.push(address);
  }
  if (city) {
    result.push(city);
  }
  if (state) {
    result.push(state);
  }
  if (zip) {
    result.push(zip);
  }

  if (result.length === 0) {
    return '--';
  }
  return result.join(', ');
};

/**
 * Resolves the address printed for the interpreting physician on a report. Formats the physician's own contact when includeContactToReport is set and the facility contact otherwise, passing isAIReport through as the flag deciding whether the street line is kept.
 *
 * @param physician - Interpreting physician with contact and includeContactToReport.
 * @param isAIReport - Whether the street address line is included.
 * @param facilityInfo - Facility whose contact is the fallback.
 * @returns Formatted physician or facility address.
 */
const getInterrettingPhysicianAddress = (
  physician: any,
  isAIReport: boolean,
  facilityInfo: any,
) => {
  const { contact, includeContactToReport } = physician || {};
  if (!includeContactToReport) {
    return generateTextAddress(facilityInfo?.contact, isAIReport);
  }
  return generateTextAddress(contact, isAIReport);
};

/**
 * Stamps the signed-in interpreting physician's name, phone and address onto a report PDF page.
 * Picks the AI or EOU layout from the format versions, blanks the physician box, then draws the
 * name without title on AI reports, the phone and the address from the physician or facility
 * contact, each truncated to the layout's line limits.
 *
 * @param props - Page, regular and medium fonts, format versions, facility info and new-format flag.
 * @returns Nothing.
 */
const handleUpdateInterpretationPdf = ({
  page,
  customFontMedium,
  customFont,
  formatVersion,
  aiFormatVersion,
  facilityInfo,
  isNewFormat,
}: {
  page?: any;
  customFontMedium?: any;
  isAIReportOld?: boolean;
  customFont?: any;
  formatVersion?: string;
  aiFormatVersion?: string;
  reportType?: string;
  facilityInfo?: any;
  isNewFormat?: boolean;
}) => {
  const interprettingPhysician = auth.getUserInfo();
  const isAIReport = isNewFormat || !_.isNil(aiFormatVersion);
  const isAIReportOld = _.isNil(aiFormatVersion) && isNewFormat;
  const fontSize = 9;
  const boldLineHeight = 11.5;

  const PDF_CONFIG = {
    COLOR_BLACK: rgb(0, 0, 0),
    COLOR_WHITE: rgb(1, 1, 1),
    FONT_SIZE_SMALL: 10,
    LINE_HEIGHT_SMALL: 12,
  };
  const layout = isAIReport
    ? isAIReportOld
      ? AI_REPORT_LAYOUT
      : AI_REPORT_LAYOUT_VER100
    : formatVersion === '2.0.0'
      ? EOU_REPORT_LAYOUT_VER200
      : EOU_REPORT_LAYOUT;
  const fontMediumOptions = {
    font: customFontMedium,
    color: PDF_CONFIG.COLOR_BLACK,
    size: fontSize,
  };
  const fontOptions = {
    font: customFont,
    color: PDF_CONFIG.COLOR_BLACK,
    size: fontSize,
  };

  page.drawRectangle({
    x: layout.INTER_PHYSICIAN_BOX_X,
    y: layout.BOX_Y,
    width: layout.BOX_WIDTH,
    height: layout.BOX_HEIGHT,
    color: PDF_CONFIG.COLOR_WHITE,
  });

  const titleName = getPhysicianTitle(interprettingPhysician, isAIReport);
  // AI report do not display title

  const { lines: interPhysicianLines, rowCount: interPhysicianRowCount } = truncateTextAndRowCount({
    text: titleName || '--',
    font: customFontMedium,
    fontSize,
    maxWidth: layout.MAX_TEXT_WIDTH,
    maxLines: layout.MAX_NAME_LINES,
  });
  drawLines({
    page,
    lines: interPhysicianLines,
    x: layout.INTER_PHYSICIAN_X,
    y: layout.PHYSICIAN_BASE_Y,
    lineHeight: boldLineHeight,
    options: fontMediumOptions,
  });

  const interPhone = getInterrettingPhysicianPhone(interprettingPhysician, facilityInfo) || '--';
  page.drawText(interPhone, {
    ...fontOptions,
    x: layout.INTER_PHYSICIAN_X,
    y: layout.PHYSICIAN_BASE_Y - layout.ROW_HEIGHT - (interPhysicianRowCount - 1) * boldLineHeight,
  });
  const interprettingPhysicianAddress = getInterrettingPhysicianAddress(
    interprettingPhysician,
    isAIReport,
    facilityInfo,
  );
  const { lines: interAddressLines } = truncateTextAndRowCount({
    text: interprettingPhysicianAddress || '--',
    font: customFont,
    fontSize,
    maxWidth: layout.MAX_TEXT_WIDTH,
    maxLines: layout.MAX_ADDRESS_LINES,
  });

  drawLines({
    page,
    lines: interAddressLines,
    x: layout.INTER_PHYSICIAN_X,
    y:
      layout.PHYSICIAN_BASE_Y -
      layout.ROW_HEIGHT * 2 -
      (interPhysicianRowCount - 1) * boldLineHeight,
    lineHeight: PDF_CONFIG.LINE_HEIGHT_SMALL,
    options: {
      ...fontOptions,
    },
  });
};

/**
 * Assembles the title and content pairs shown in the report details header. Returns an empty array without a report, otherwise emits the translated type, the linked study id, a report id taken from eventFid for Event reports or reportId for Interim, UNREVIEWED and Notification ones, the linked patient, the physician, event time or date of service, and the facility name.
 *
 * @param clickedReport - Report currently opened in the details view.
 * @returns Title blocks, some carrying a hyperlink id.
 */
const generateTitleBlockArrayReportDetails = (clickedReport: any) => {
  if (!clickedReport) {
    return [];
  }

  const titleBlockArray = [
    {
      title: t`Type`,
      content:
        clickedReport.type === 'Study'
          ? t`End of Use`
          : clickedReport.type === 'Interim'
            ? t`Interim`
            : clickedReport.type === 'Notification'
              ? t`Notification`
              : clickedReport.type === 'UNREVIEWED'
                ? t`Instant`
                : gettextReportType(clickedReport.type),
    },
    {
      title: t`Study ID`,
      content: zeroPad(clickedReport.studyFid),
      id: 'hyperlink-btn-study',
    },
  ];

  if (clickedReport.type === 'Event') {
    titleBlockArray.push({
      title: t`Report ID`,
      content: zeroPad(clickedReport.eventFid),
    });
  } else if (_.includes(['Interim', 'UNREVIEWED', 'Notification'], clickedReport.type)) {
    titleBlockArray.push({
      title: t`Report ID`,
      content: zeroPad(clickedReport.reportId),
    });
  }

  titleBlockArray.push(
    {
      title: t`Patient`,
      content: clickedReport.patientName,
      id: 'hyperlink-btn-patient',
    },
    {
      title: t`Physician`,
      content: clickedReport.interprettingPhysicianName,
    },
  );

  if (clickedReport.type === 'Event') {
    titleBlockArray.push({
      title: t`Event time`,
      content: clickedReport.eventTime,
    });
  } else {
    titleBlockArray.push({
      title: t`Date of service`,
      content: clickedReport.dateOfService,
    });
  }

  titleBlockArray.push({
    title: t`Facility`,
    content: clickedReport.facility?.name,
  });

  return titleBlockArray;
};

export {
  checkShouldTrackAddSignatureTime,
  drawFindingOnEventStrip,
  generateTitleBlockArrayReportDetails,
  getModifyPdfReportOptions,
  getReportFileName,
  truncateFindingsText,
  handleUpdateInterpretationPdf,
};
